import { FormEvent, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage, blobErrorMessage } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { Link } from 'react-router-dom';
import type { Company, NfseEmission, NfsePreview, Tomador } from '../api/types';

const DESCRICAO_PADRAO = 'DESENVOLVIMENTO DE PROGRAMAS DE COMPUTADOR COM FINALIDADE PARA O EXTERIOR';

/** Significados conhecidos de códigos, exibidos ao lado do número. */
const COD_TRIB_NAC: Record<string, string> = {
  '010101': 'Análise e desenvolvimento de sistemas',
};
const COD_NBS: Record<string, string> = {
  '112019000': 'Pesquisa e desenvolvimento em ciências, engenharia e tecnologia',
};
/** "010101" → "010101 (Análise e desenvolvimento de sistemas)" quando conhecido. */
function withMeaning(code: string | null | undefined, map: Record<string, string>): string {
  if (!code) return '—';
  return map[code] ? `${code} (${map[code]})` : code;
}

/** Máscara estilo "centavos": os 2 últimos dígitos são sempre decimais. Ex: 12585 → 125.85; 525 → 5.25. */
function maskDecimal2(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return (parseInt(digits, 10) / 100).toFixed(2);
}

/** Dinheiro em formato BR p/ exibição: R$ 12.585,00 (milhar com ., decimal com ,). */
function maskMoneyBR(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const n = parseInt(digits, 10) / 100;
  return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Converte o display BR (R$ 12.585,00) pro formato que o backend espera (12585.00). */
function moneyToBackend(display: string): string {
  const digits = display.replace(/\D/g, '');
  if (!digits) return '';
  return (parseInt(digits, 10) / 100).toFixed(2);
}

/** Mês atual no formato YYYY-MM (horário local). */
function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "junho de 2026" → "Junho de 2026". */
function monthLabel(iso: string): string {
  const s = new Date(iso).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Agrupa emissões (já ordenadas desc) por mês/ano, preservando a ordem. */
function groupByMonth(items: NfseEmission[]): { label: string; items: NfseEmission[] }[] {
  const groups: { label: string; items: NfseEmission[] }[] = [];
  for (const e of items) {
    const label = monthLabel(e.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(e);
    else groups.push({ label, items: [e] });
  }
  return groups;
}

const STATUS_STYLE: Record<string, string> = {
  autorizado: 'text-emerald-600',
  erro: 'text-red-600',
  cancelado: 'text-slate-500',
  pendente: 'text-amber-600',
};

export default function NfsePage() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data: tomadores } = useQuery({
    queryKey: ['tomadores'],
    queryFn: async () => (await api.get<Tomador[]>('/nfse/tomadores')).data,
  });
  const { data: emissions } = useQuery({
    queryKey: ['nfse-emissions'],
    queryFn: async () => (await api.get<NfseEmission[]>('/nfse/emissions')).data,
  });
  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: async () => (await api.get<Company>('/company')).data,
  });
  // Só libera emissão quando a empresa está cadastrada (CNPJ + certificado).
  const empresaPronta = !!company && company.exists && !!company.cnpj && company.has_cert;

  // --- Emissão ---
  const [tomadorId, setTomadorId] = useState('');
  const [valor, setValor] = useState('');
  const [aliquota, setAliquota] = useState('');
  const [descricao, setDescricao] = useState(DESCRICAO_PADRAO);
  const [competencia, setCompetencia] = useState(currentMonth);  // YYYY-MM
  const [preview, setPreview] = useState<NfsePreview | null>(null);
  const [showXml, setShowXml] = useState(false);

  // Alíquota padrão = a última usada (sobe um pouco a cada mês). Pega a emissão
  // mais recente com alíquota e pré-preenche, sem sobrescrever o que o usuário digitar.
  const lastAliquota = emissions?.find((e) => e.aliquota != null)?.aliquota;
  useEffect(() => {
    if (lastAliquota != null) setAliquota((a) => (a === '' ? lastAliquota.toFixed(2) : a));
  }, [lastAliquota]);

  const doPreview = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<NfsePreview>('/nfse/preview', {
        tomador_id: tomadorId, valor: moneyToBackend(valor), aliquota, descricao, competencia,
      });
      return data;
    },
    onSuccess: (p) => { setPreview(p); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha no preview')),
  });

  const doEmit = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<NfseEmission>('/nfse/emit', {
        tomador_id: tomadorId, valor: moneyToBackend(valor), aliquota, descricao, competencia,
      });
      return data;
    },
    onSuccess: (e) => {
      qc.invalidateQueries({ queryKey: ['nfse-emissions'] });
      setPreview(null);
      // alíquota volta a '' e o efeito recarrega a última usada (a desta emissão).
      setValor(''); setAliquota(''); setDescricao(DESCRICAO_PADRAO);
      if (e.status === 'autorizado') {
        toast.success(`NFS-e autorizada · chave ${e.chave_acesso}`);
      } else {
        toast.error(`Emissão com status "${e.status}": ${e.erro || ''}`);
      }
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha na emissão')),
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const motivo = prompt('Motivo do cancelamento:') || '';
      return (await api.post(`/nfse/emissions/${id}/cancel`, { motivo })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nfse-emissions'] }); toast.success('NFS-e cancelada'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao cancelar')),
  });

  const removeEmission = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/nfse/emissions/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nfse-emissions'] }); toast.success('Emissão apagada'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao apagar')),
  });

  const clearErrors = useMutation({
    mutationFn: async () => (await api.post('/nfse/emissions/clear-errors', {})).data,
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ['nfse-emissions'] }); toast.success(`${r.deleted} emissão(ões) com erro apagada(s)`); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao limpar')),
  });

  const downloadXml = useMutation({
    mutationFn: async (e: NfseEmission) => {
      const res = await api.get(`/nfse/emissions/${e.id}/xml`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `nfse-${e.num_dps}.xml`; a.click();
      URL.revokeObjectURL(url);
    },
    onError: async (err: any) => toast.error(await blobErrorMessage(err, 'Falha ao baixar XML')),
  });

  const downloadDanfse = useMutation({
    mutationFn: async (e: NfseEmission) => {
      const res = await api.get(`/nfse/emissions/${e.id}/danfse`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `danfse-${e.chave_acesso || e.num_dps}.pdf`; a.click();
      URL.revokeObjectURL(url);
    },
    onError: async (err: any) => toast.error(await blobErrorMessage(err, 'Falha ao baixar DANFSE')),
  });

  function submitPreview(ev: FormEvent) {
    ev.preventDefault();
    doPreview.mutate();
  }

  function confirmEmit() {
    const isProd = preview?.ambiente === 'producao';
    const msg = isProd
      ? '⚠️ PRODUÇÃO — emissão fiscal IRREVERSÍVEL. Confirma emitir esta NFS-e?'
      : 'Emitir esta NFS-e (produção restrita / teste)?';
    if (confirm(msg)) doEmit.mutate();
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">NFS-e</h2>

      {!empresaPronta && (
        <div className="card text-sm text-amber-800 bg-amber-50 space-y-1">
          <div className="font-semibold">Configure sua empresa antes de emitir</div>
          <div>
            Preencha o CNPJ e envie o certificado A1 na tela{' '}
            <Link to="/empresa" className="text-brand-600 underline font-medium">Empresa</Link> pra liberar a emissão de NFS-e.
          </div>
        </div>
      )}

      {empresaPronta && (!tomadores || tomadores.length === 0) && (
        <div className="card text-sm text-amber-700 bg-amber-50">
          Cadastre um tomador abaixo antes de emitir.
        </div>
      )}

      {/* Emissão */}
      {empresaPronta && (
      <form onSubmit={submitPreview} className="card space-y-3">
        <h3 className="font-semibold">Emitir nota</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <label className="block sm:col-span-2">
            Tomador
            <select className="input" value={tomadorId} onChange={(e) => setTomadorId(e.target.value)} required>
              <option value="">— selecione —</option>
              {tomadores?.map((t) => (
                <option key={t.id} value={t.id}>{t.nome} — {t.razao_social}</option>
              ))}
            </select>
          </label>
          <label className="block">
            Valor
            <input className="input" type="text" inputMode="numeric" value={valor} onChange={(e) => setValor(maskMoneyBR(e.target.value))} placeholder="R$ 0,00" required />
          </label>
          <label className="block">
            Alíquota (%) <span className="text-slate-400">vem da última usada</span>
            <input className="input" type="text" inputMode="numeric" value={aliquota} onChange={(e) => setAliquota(maskDecimal2(e.target.value))} placeholder="0.00" />
          </label>
          <label className="block">
            Competência <span className="text-slate-400">(mês/ano — pode ser retroativo)</span>
            <input
              className="input w-full min-w-0 appearance-none"
              type="month"
              value={competencia}
              max={currentMonth()}
              onChange={(e) => setCompetencia(e.target.value)}
              required
            />
          </label>
          <label className="block sm:col-span-2">
            Descrição do serviço
            <input className="input" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </label>
        </div>
        <button type="submit" className="btn btn-primary text-sm" disabled={doPreview.isPending}>
          {doPreview.isPending ? 'Gerando…' : 'Pré-visualizar'}
        </button>
      </form>
      )}

      {/* Preview + confirmação */}
      {preview && (
        <div className="card space-y-2 border-2 border-brand-200">
          <h3 className="font-semibold">Confirmar emissão</h3>
          <div className={`text-xs inline-block rounded px-2 py-0.5 ${preview.ambiente === 'producao' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
            ambiente: {preview.ambiente}{preview.ambiente === 'producao' && ' — IRREVERSÍVEL'}
          </div>
          <dl className="text-sm grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
            <dt className="text-slate-500">Prestador</dt><dd>{preview.prestador.nome} (CNPJ {preview.prestador.cnpj})</dd>
            <dt className="text-slate-500">Tomador</dt><dd>{preview.tomador.razao_social}</dd>
            <dt className="text-slate-500">Descrição</dt><dd>{preview.descricao}</dd>
            <dt className="text-slate-500">Valor</dt><dd>R$ {preview.valor}</dd>
            <dt className="text-slate-500">Alíquota</dt><dd>{preview.aliquota}%</dd>
            <dt className="text-slate-500">ISS</dt><dd>R$ {preview.iss} ({preview.servico.iss_retido ? 'retido' : 'não retido'})</dd>
            <dt className="text-slate-500">Item lista serv.</dt><dd>{withMeaning(preview.servico.item_lista_servico, COD_TRIB_NAC)}</dd>
            <dt className="text-slate-500">Cód. trib. munic.</dt><dd>{preview.servico.cod_atividade || '—'}</dd>
            <dt className="text-slate-500">NBS</dt><dd>{withMeaning(preview.servico.nbs, COD_NBS)}</dd>
            <dt className="text-slate-500">Competência</dt><dd>{preview.competencia}</dd>
            <dt className="text-slate-500">Tributado em</dt><dd>{preview.servico.municipio_nome}</dd>
            <dt className="text-slate-500">DPS nº</dt><dd>{preview.num_dps}</dd>
          </dl>
          <button className="text-xs text-brand-600 hover:underline" onClick={() => setShowXml(!showXml)}>
            {showXml ? 'Ocultar' : 'Ver'} XML assinado da DPS
          </button>
          {showXml && (
            <pre className="text-[10px] bg-slate-900 text-slate-100 rounded p-2 overflow-auto max-h-64">{preview.dps_xml_assinado}</pre>
          )}
          <div className="flex gap-2 pt-1">
            <button className="btn btn-primary text-sm" disabled={doEmit.isPending} onClick={confirmEmit}>
              {doEmit.isPending ? 'Emitindo…' : 'Confirmar e emitir'}
            </button>
            <button className="btn btn-secondary text-sm" onClick={() => setPreview(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Emissões */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Emissões</h3>
          {emissions?.some((e) => e.status === 'erro') && (
            <button
              className="text-xs text-red-600 hover:underline disabled:opacity-50"
              disabled={clearErrors.isPending}
              onClick={() => { if (confirm('Apagar todas as emissões com erro?')) clearErrors.mutate(); }}
            >
              Limpar erros
            </button>
          )}
        </div>
        {emissions?.length === 0 && <div className="card text-sm text-slate-600 text-center">Nenhuma emissão ainda.</div>}
        {emissions && groupByMonth(emissions).map((group) => (
        <div key={group.label} className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-2">
            {group.label} <span className="font-normal normal-case">· {group.items.length} nota{group.items.length > 1 ? 's' : ''}</span>
          </h4>
          {group.items.map((e) => (
          <div key={e.id} className="card flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium flex items-center gap-2 flex-wrap">
                #{e.num_dps} · {e.tomador_snapshot?.razao_social || '—'}
                <span className={`text-xs ${STATUS_STYLE[e.status] || ''}`}>● {e.status}</span>
                <span className="text-xs text-slate-400">{e.ambiente}</span>
              </div>
              <div className="text-xs text-slate-500 break-words">
                R$ {e.valor?.toFixed(2)} · ISS R$ {e.iss?.toFixed(2)}
                · {new Date(e.created_at).toLocaleString('pt-BR')}
                {e.chave_acesso && <div className="break-all text-slate-400">chave {e.chave_acesso}</div>}
              </div>
              {e.erro && <div className="text-xs text-red-600 break-words">{e.erro}</div>}
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              {e.status === 'autorizado' && (
                <button className="btn btn-secondary text-xs py-1" onClick={() => downloadDanfse.mutate(e)} disabled={downloadDanfse.isPending}>
                  {downloadDanfse.isPending && downloadDanfse.variables?.id === e.id ? '…' : 'DANFSE'}
                </button>
              )}
              <button className="btn btn-secondary text-xs py-1" onClick={() => downloadXml.mutate(e)} disabled={downloadXml.isPending}>XML</button>
              {e.status === 'autorizado' && (
                <button className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed px-2" onClick={() => cancel.mutate(e.id)} disabled={cancel.isPending}>
                  Cancelar
                </button>
              )}
              {e.status !== 'autorizado' && (
                <button className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed px-2" onClick={() => { if (confirm('Apagar esta emissão?')) removeEmission.mutate(e.id); }} disabled={removeEmission.isPending}>
                  Apagar
                </button>
              )}
            </div>
          </div>
          ))}
        </div>
        ))}
      </div>

      <TomadoresSection />
    </div>
  );
}

function TomadoresSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: tomadores } = useQuery({
    queryKey: ['tomadores'],
    queryFn: async () => (await api.get<Tomador[]>('/nfse/tomadores')).data,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Tomador>>({ estrangeiro: false, tipo_doc: 'CNPJ' });

  const save = useMutation({
    mutationFn: async () =>
      form.id
        ? (await api.put(`/nfse/tomadores/${form.id}`, form)).data
        : (await api.post('/nfse/tomadores', form)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tomadores'] });
      setForm({ estrangeiro: false, tipo_doc: 'CNPJ' });
      setOpen(false);
      toast.success('Tomador salvo');
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao salvar tomador')),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/nfse/tomadores/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tomadores'] }); toast.success('Tomador removido'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao remover tomador')),
  });

  function set<K extends keyof Tomador>(k: K, v: Tomador[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function novoTomador() {
    setForm({ estrangeiro: false, tipo_doc: 'CNPJ' });
    setOpen(true);
  }
  function editarTomador(t: Tomador) {
    setForm({ ...t });
    setOpen(true);
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Tomadores</h3>
        <button className="btn btn-secondary text-sm" onClick={() => (open ? setOpen(false) : novoTomador())}>
          {open ? 'Fechar' : '+ Tomador'}
        </button>
      </div>

      {open && (
        <form
          className="space-y-2 text-sm border-b pb-3"
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        >
          <h4 className="font-medium">{form.id ? 'Editar tomador' : 'Novo tomador'}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input className="input" placeholder="Apelido (busca)" value={form.nome || ''} onChange={(e) => set('nome', e.target.value)} required />
            <input className="input" placeholder="Razão social" value={form.razao_social || ''} onChange={(e) => set('razao_social', e.target.value)} required />
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.estrangeiro} onChange={(e) => set('estrangeiro', e.target.checked)} /> Estrangeiro
            </label>
            {!form.estrangeiro && (
              <select className="input" value={form.tipo_doc || 'CNPJ'} onChange={(e) => set('tipo_doc', e.target.value)}>
                <option value="CNPJ">CNPJ</option>
                <option value="CPF">CPF</option>
              </select>
            )}
            <input
              className="input"
              placeholder={form.estrangeiro ? 'NIF / Tax ID (deixe vazio se não tiver)' : 'Documento'}
              value={form.documento || ''}
              onChange={(e) => set('documento', e.target.value)}
            />
            <input className="input" placeholder="Alíquota padrão (%)" value={form.aliquota_padrao ?? ''} onChange={(e) => set('aliquota_padrao', Number(e.target.value) as any)} />
            {!form.estrangeiro ? (
              <>
                <input className="input" placeholder="Cód. município IBGE" value={form.cod_municipio || ''} onChange={(e) => set('cod_municipio', e.target.value)} />
                <input className="input" placeholder="CEP" value={form.cep || ''} onChange={(e) => set('cep', e.target.value)} />
                <input className="input" placeholder="Logradouro" value={form.logradouro || ''} onChange={(e) => set('logradouro', e.target.value)} />
                <input className="input" placeholder="Número" value={form.numero || ''} onChange={(e) => set('numero', e.target.value)} />
                <input className="input" placeholder="Bairro" value={form.bairro || ''} onChange={(e) => set('bairro', e.target.value)} />
              </>
            ) : (
              <>
                <input className="input" placeholder="Cód. país ISO 2 letras (ex: US)" maxLength={2} value={form.cod_pais || ''} onChange={(e) => set('cod_pais', e.target.value.toUpperCase())} />
                <input className="input" placeholder="País" value={form.pais || ''} onChange={(e) => set('pais', e.target.value)} />
                <input className="input" placeholder="Cidade" value={form.cidade_ext || ''} onChange={(e) => set('cidade_ext', e.target.value)} />
                <input className="input" placeholder="Estado/Província (ex: TEX)" maxLength={3} value={form.uf || ''} onChange={(e) => set('uf', e.target.value)} />
                <input className="input" placeholder="ZIP / Cód. postal (5 díg.)" value={form.cep || ''} onChange={(e) => set('cep', e.target.value)} />
                <input className="input" placeholder="Logradouro" value={form.logradouro || ''} onChange={(e) => set('logradouro', e.target.value)} />
                <input className="input" placeholder="Número" value={form.numero || ''} onChange={(e) => set('numero', e.target.value)} />
                <input className="input" placeholder="Bairro" value={form.bairro || ''} onChange={(e) => set('bairro', e.target.value)} />
              </>
            )}
          </div>
          <button type="submit" className="btn btn-primary text-sm" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar tomador'}
          </button>
        </form>
      )}

      <div className="space-y-1">
        {tomadores?.map((t) => (
          <div key={t.id} className="flex items-center justify-between text-sm">
            <span>
              <strong>{t.nome}</strong> — {t.razao_social}
              {t.estrangeiro && <span className="text-xs text-slate-400"> (estrangeiro · {t.cod_pais || '?'})</span>}
            </span>
            <div className="flex gap-3 shrink-0">
              <button className="text-xs text-brand-600 hover:underline" onClick={() => editarTomador(t)}>
                editar
              </button>
              <button className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed" disabled={remove.isPending} onClick={() => { if (confirm(`Remover ${t.nome}?`)) remove.mutate(t.id); }}>
                remover
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
